import { type Handle, css } from 'remix/component'
import { on } from './event-mixin.ts'
import {
	handleForkOutdatedCopyClick,
	handleForkOutdatedCopyFocusOut,
	handleForkOutdatedCopyKeyDown,
	handleForkOutdatedCopyPointerOut,
} from './fork-outdated-copy.ts'
import { clientRouteLoaders, clientRoutes } from './routes/index.tsx'
// Path-only helper: importing blog-post.tsx would pull Shiki onto `/`.
import { getSlugFromPathname } from './routes/blog-post-path.ts'
import { isCommunityListingPathname } from './routes/community-detail-shared.ts'
import {
	isOnSsrUrl,
	listenToRouterMutations,
	listenToRouterNavigation,
	listenToRouterNavigationEnd,
	matchRoute,
	navigate,
	registerClientRoutes,
	registerRouteLoaders,
	Router,
} from './client-router.tsx'
import { AppSessionProvider } from './app-session-context.tsx'
import { AppLoaderDataProvider } from './loader-data-context.tsx'
import { NavigationProgress } from './navigation-progress.tsx'
import { readRouterPathname, readRouterSearch } from './router-location.tsx'
import { ScrollRestoration } from './scroll-restoration.tsx'
import { isFeatureFlagEnabled } from './feature-flags.ts'
import {
	fetchSessionInfo,
	getSessionDisplayName,
	setSessionRefreshHandler,
	type SessionInfo,
	type SessionStatus,
} from './session.ts'
import {
	primaryLinkCss,
	visuallyHiddenUntilFocusedCss,
} from '#universal/styles/style-primitives.ts'
import { SiteFooter } from './site-footer.tsx'
import { SiteHeader } from './site-header.tsx'
import { Toaster } from './toaster.tsx'
import { acquisitionPageSummaries } from '#universal/acquisition/metadata.ts'
import { isDocsPagePath } from '#universal/docs-nav.ts'
import { type AppLoaderData } from '#universal/loader-data.ts'
import { isPackageFilesPathname } from '#universal/package-files.ts'
import { isProfilePathname } from '#universal/profile-path.ts'
import { routes } from '#universal/routes.ts'
import { userHasRole } from '#universal/permissions.ts'
import { buildAuthLink } from './auth-links.ts'
import { colors, mq, spacing, typography } from '#universal/styles/tokens.ts'
import { NotFoundPage } from './not-found-page.tsx'
import { InternalErrorPage } from './internal-error-page.tsx'
import { YouTubeWatchOverlay } from './youtube-watch-overlay.tsx'
import { scheduleConsumeAccountCreatedFathomSignal } from './fathom-events.ts'
import { stripHomeOgQueryFromLocation } from './strip-home-og-query.ts'
import {
	captureFirstTouchAttributionFromLocation,
	clearStoredFirstTouchAttribution,
} from './first-touch-attribution.ts'
import { persistReferralCookieFromLocation } from './referral-cookie.ts'
import { createScarfPageTracker } from './scarf-analytics.ts'

registerRouteLoaders(clientRouteLoaders)
registerClientRoutes(clientRoutes)

type AppProps = {
	embeddedSession?: SessionInfo | null
	loaderData?: AppLoaderData
	notFound?: boolean
	unauthorized?: boolean
	internalError?: boolean
}

function isAcquisitionPath(pathname: string) {
	return acquisitionPageSummaries.some((page) => page.path === pathname)
}

function isRedesignedMarketingPath(pathname: string) {
	return (
		pathname === '/' ||
		pathname === '/features' ||
		pathname === '/features/memory' ||
		pathname === '/features/secrets' ||
		pathname === '/features/packages' ||
		pathname === '/features/triggers' ||
		pathname === '/features/integrations' ||
		pathname === '/features/apps' ||
		pathname === '/for/business' ||
		pathname === '/pricing' ||
		pathname === '/faq' ||
		pathname === '/case-studies' ||
		pathname === '/blog' ||
		pathname === '/community' ||
		pathname === '/onboarding' ||
		pathname.startsWith('/onboarding/step-') ||
		isDocsPagePath(pathname) ||
		isProfilePathname(pathname) ||
		isCommunityListingPathname(pathname) ||
		getSlugFromPathname(pathname) !== null ||
		isAcquisitionPath(pathname)
	)
}

function isIllustratedErrorPath(pathname: string) {
	return (
		pathname === routes.notFoundPage.href() ||
		pathname === routes.internalErrorPage.href()
	)
}

/**
 * `<main>` padding is skipped when the route (or the SSR 404 / 500 for this
 * URL) owns its own gutters. The server's `notFound` / `internalError` flags
 * are sticky on the document for the session, so they only apply while we
 * are still on the URL the server rendered — same rule as `Router`. Explicit
 * `/404` and `/500` own gutters on SPA navigation too: they are matched
 * routes, so they would otherwise pick up `<main>` padding.
 */
export function appMainOwnsItsGutters(input: {
	pathname: string
	notFound: boolean
	internalError?: boolean
	onSsrUrl: boolean
}) {
	return (
		((input.notFound || input.internalError === true) && input.onSsrUrl) ||
		isIllustratedErrorPath(input.pathname) ||
		isRedesignedMarketingPath(input.pathname) ||
		isPackageFilesPathname(input.pathname) ||
		matchRoute(input.pathname, clientRoutes) == null
	)
}

export function App(handle: Handle<AppProps>) {
	let session: SessionInfo | null = handle.props.embeddedSession ?? null
	let sessionStatus: SessionStatus =
		handle.props.embeddedSession !== undefined ? 'ready' : 'idle'
	let sessionRefreshInFlight = false
	let sessionRefreshQueued = false
	let lastSessionRefreshAt = 0
	let sessionMaybeStale = false
	let currentPathname = readRouterPathname(handle)
	let lastFocusManagedPathname = currentPathname

	// Navigation-triggered refreshes are throttled: auth rarely changes
	// mid-session and every SPA navigation was previously a /session round
	// trip (2 D1 queries). Refreshes after mutations (router form POSTs such
	// as logout) and explicit refreshes (profile updates via
	// setSessionRefreshHandler) always bypass the throttle.
	const sessionRefreshThrottleMs = 30_000

	function queueSessionRefresh() {
		sessionRefreshQueued = true
		if (sessionRefreshInFlight) return

		if (sessionStatus === 'idle') {
			sessionStatus = 'loading'
			handle.update()
		}

		sessionRefreshQueued = false
		sessionRefreshInFlight = true
		handle.queueTask(async (signal) => {
			const nextSession = await fetchSessionInfo(signal)
			sessionRefreshInFlight = false
			if (signal.aborted) return
			lastSessionRefreshAt = Date.now()
			session = nextSession
			sessionStatus = 'ready'
			handle.update()
			if (sessionRefreshQueued) {
				queueSessionRefresh()
			}
		})
		if (sessionStatus !== 'loading') {
			handle.update()
		}
	}

	function queueThrottledSessionRefresh() {
		if (
			!sessionMaybeStale &&
			sessionStatus === 'ready' &&
			Date.now() - lastSessionRefreshAt < sessionRefreshThrottleMs
		) {
			return
		}
		sessionMaybeStale = false
		queueSessionRefresh()
	}

	if (typeof document !== 'undefined') {
		setSessionRefreshHandler(queueSessionRefresh)
		const trackScarfPage = createScarfPageTracker()
		if (
			!handle.props.notFound &&
			!handle.props.unauthorized &&
			!handle.props.internalError
		) {
			trackScarfPage()
		}
		listenToRouterNavigationEnd(handle, trackScarfPage)
		// Capture UTMs from any landing URL before homepage CTAs rewrite them.
		// Referral share links write a last-wins one-week cookie separately.
		captureFirstTouchAttributionFromLocation()
		persistReferralCookieFromLocation()
		// New-account signal: drop tab-scoped first-touch so a later signup in
		// this tab cannot inherit the previous visitor's campaign.
		try {
			if (
				new URL(window.location.href).searchParams.get('accountCreated') === '1'
			) {
				clearStoredFirstTouchAttribution()
			}
		} catch {
			// ignore malformed locations
		}
		// Fathom loads deferred; retry briefly so OAuth ?accountCreated=1 is not dropped.
		scheduleConsumeAccountCreatedFathomSignal()
		// Share links carry `?og=` for crawlers. Humans should not keep it.
		stripHomeOgQueryFromLocation()
		if (handle.props.embeddedSession === undefined) {
			handle.queueTask(() => {
				queueSessionRefresh()
			})
		} else {
			lastSessionRefreshAt = Date.now()
		}
		listenToRouterNavigation(handle, () => {
			currentPathname = readRouterPathname(handle)
			persistReferralCookieFromLocation()
			queueThrottledSessionRefresh()
			handle.update()
		})
		listenToRouterNavigationEnd(handle, (detail) => {
			stripHomeOgQueryFromLocation()
			const nextPathname = new URL(detail.location, window.location.origin)
				.pathname
			if (nextPathname === lastFocusManagedPathname) return
			lastFocusManagedPathname = nextPathname
			handle.queueTask(() => {
				// Docs guide changes: announce the new article title, not the
				// shared `<main>` landmark that already had focus.
				const heading = document.querySelector('[data-docs-heading]')
				const main = document.getElementById('main')
				const target =
					heading instanceof HTMLElement
						? heading
						: main instanceof HTMLElement
							? main
							: null
				if (!target) return
				target.focus({ preventScroll: true })
			})
		})
		// Router form POSTs mutate server state, which may include auth (e.g.
		// logout destroys the session cookie), so the next navigation must
		// bypass the refresh throttle. The refresh cannot start here: the
		// follow-up redirect navigation re-renders the shell, which aborts
		// in-flight queued tasks and would silently drop the refresh.
		listenToRouterMutations(handle, () => {
			sessionMaybeStale = true
		})
	}

	return () => {
		currentPathname = readRouterPathname(handle)
		const sessionEmail = session?.email ?? ''
		const sessionDisplayName = getSessionDisplayName(session)
		const isSessionReady = sessionStatus === 'ready'
		const isLoggedIn = isSessionReady && Boolean(sessionEmail)
		const showAuthLinks = isSessionReady && !isLoggedIn
		const showAdminLink =
			isLoggedIn && session != null && userHasRole(session, 'admin')
		const showDemoIndicator = isFeatureFlagEnabled(session, 'demo-indicator')
		const oauthRedirectTo =
			currentPathname === '/oauth/authorize'
				? `${currentPathname}${readRouterSearch(handle)}`
				: null
		const loginHref = buildAuthLink('/login', oauthRedirectTo)
		// Redesigned pages own their own layout (gutters, measures, max-width
		// container), so `<main>` must not add its generic padding on top. The
		// landing page also owns its own signup close (the "Give your services
		// a home" section). The redesigned auth screens (login/signup) are a
		// standalone two-panel canvas with their own brand link, theme toggle,
		// and "back" corner — the prototype renders them without the site
		// chrome, so the header/footer stand down entirely there.
		const isAuthShellPath =
			currentPathname === '/login' || currentPathname === '/signup'
		const routeOwnsItsGutters = appMainOwnsItsGutters({
			pathname: currentPathname,
			notFound: handle.props.notFound === true,
			internalError: handle.props.internalError === true,
			onSsrUrl: isOnSsrUrl(handle),
		})

		return (
			<AppLoaderDataProvider loaderData={handle.props.loaderData}>
				<AppSessionProvider session={session} status={sessionStatus}>
					<NavigationProgress />
					<ScrollRestoration />
					<div
						data-app-frame
						mix={css({
							width: '100%',
							minHeight: '100vh',
							display: 'flex',
							flexDirection: 'column',
							fontFamily: typography.fontFamily,
							boxSizing: 'border-box',
							/*
							 * The redesign's decorative glows are pseudo-elements with
							 * negative horizontal insets (`heroArtCss`, `pageHeadCss`, and
							 * the per-page overrides that borrow them), so they bleed past
							 * the viewport by design. Left unclipped that bleed becomes real
							 * horizontal page scroll on a phone — measured at 12-66px across
							 * the marketing routes — which also knocks the fixed mobile-menu
							 * popover out of line with the sticky header once you swipe
							 * sideways. `clip` rather than `hidden`: it contains the bleed
							 * without creating a scroll container, so the sticky header still
							 * resolves against the viewport and vertical bleed still shows.
							 */
							overflowX: 'clip',
						})}
					>
						<a
							href="#main"
							mix={[
								on('click', (event) => {
									const main = document.getElementById('main')
									if (!(main instanceof HTMLElement)) return
									event.preventDefault()
									navigate('#main')
									main.focus({ preventScroll: true })
									main.scrollIntoView()
								}),
								css(visuallyHiddenUntilFocusedCss),
							]}
						>
							Skip to content
						</a>
						{isAuthShellPath ||
						currentPathname === routes.business.href() ? null : (
							<SiteHeader
								loggedIn={isLoggedIn}
								displayName={sessionDisplayName}
								username={session?.username ?? ''}
								avatarUrl={session?.avatarUrl ?? null}
								showAdminLink={showAdminLink}
								showDemoIndicator={isLoggedIn && showDemoIndicator}
								loginHref={loginHref}
								currentPathname={currentPathname}
								organizations={session?.organizations}
								inviteCount={session?.inviteCount}
								lastUsedOrganization={session?.lastUsedOrganization}
							/>
						)}
						<main
							id="main"
							tabIndex={-1}
							mix={[
								on('click', (event) => {
									void handleForkOutdatedCopyClick(event)
								}),
								on('keydown', handleForkOutdatedCopyKeyDown),
								on('focusout', handleForkOutdatedCopyFocusOut),
								on('pointerout', handleForkOutdatedCopyPointerOut),
								css(
									isAuthShellPath
										? {
												width: '100%',
												boxSizing: 'border-box',
												flex: 1,
												viewTransitionName: 'page',
												// The auth canvas stretches to fill the shell column.
												display: 'grid',
											}
										: routeOwnsItsGutters
											? {
													width: '100%',
													boxSizing: 'border-box',
													flex: 1,
													viewTransitionName: 'page',
												}
											: {
													width: '100%',
													boxSizing: 'border-box',
													flex: 1,
													viewTransitionName: 'page',
													padding: `${spacing.lg} ${spacing.xl} ${spacing.sm}`,
													[mq.tablet]: {
														padding: `${spacing.sm} ${spacing.sm} 0`,
													},
													[mq.mobile]: {
														padding: `${spacing.md} ${spacing.md} ${spacing.sm}`,
													},
												},
								),
							]}
						>
							<Router
								routes={clientRoutes}
								loaderData={handle.props.loaderData}
								notFound={handle.props.notFound}
								unauthorized={handle.props.unauthorized}
								internalError={handle.props.internalError}
								fallback={<NotFoundPage />}
								internalErrorFallback={<InternalErrorPage />}
								unauthorizedFallback={
									<section>
										<h1
											mix={css({
												fontSize: typography.fontSize.lg,
												fontWeight: typography.fontWeight.semibold,
												marginBottom: spacing.sm,
												color: colors.text,
											})}
										>
											Unauthorized
										</h1>
										<p mix={css({ color: colors.textMuted })}>
											You are not allowed to view this page.
										</p>
										<p
											mix={css({
												display: 'flex',
												gap: spacing.md,
												flexWrap: 'wrap',
											})}
										>
											<a href="/" mix={css(primaryLinkCss)}>
												Go home
											</a>
											<a href={loginHref} mix={css(primaryLinkCss)}>
												Log in
											</a>
										</p>
									</section>
								}
							/>
						</main>
						{isAuthShellPath ? null : (
							<SiteFooter loggedIn={isLoggedIn} loginHref={loginHref} />
						)}
						<Toaster />
						<YouTubeWatchOverlay
							snapshot={handle.props.loaderData?.youtubeWatch}
						/>
					</div>
				</AppSessionProvider>
			</AppLoaderDataProvider>
		)
	}
}
